import type { KeyboardEvent } from 'react';

import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { cn } from '@/lib/utils';

export interface QuerySearchProps {
  /** Current search value */
  value: string;
  /** Callback when search value changes */
  onChange: (value: string) => void;
  /** Optional className for styling */
  className?: string;
}

/**
 * QuerySearch - Search input for filtering queries in sidebar
 *
 * Features:
 * - Magnifying glass icon (leading)
 * - Clear button when text present
 * - Full accessibility (searchbox role, ARIA attributes)
 * - Controlled input
 *
 * Based on SearchBar.tsx pattern but simplified for sidebar context.
 *
 * @example
 * ```tsx
 * const [search, setSearch] = useState('');
 * <QuerySearch value={search} onChange={setSearch} />
 * ```
 */
export const QuerySearch = ({
  value,
  onChange,
  className,
}: QuerySearchProps) => {
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      onChange('');
    }
  };

  return (
    <SearchBar
      size="sm"
      value={value}
      onChange={onChange}
      onKeyDown={handleKeyDown}
      placeholder="Search queries"
      className={cn(
        'focus-within:ring-chariot-brand-primary w-full rounded-md focus-within:ring-2 focus-within:outline-hidden',
        className
      )}
      ariaAutocomplete="list"
      dataTestId="query-search-input"
      iconTestId="query-search-icon"
    />
  );
};
